import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import complianceReportService from '../../services/complianceReportService';
import ComplianceReportCard from './components/ComplianceReportCard';
import AiComplianceAnalysisCard from './components/AiComplianceAnalysisCard';
import aiComplianceService from '../../services/aiComplianceService';
import claudeComplianceService from '../../services/claudeComplianceService';

import RemediationStatusPanel from './components/RemediationStatusPanel';
import DocumentLodgementPanel from './components/DocumentLodgementPanel';
import ReportCustomizationPanel from './components/ReportCustomizationPanel';
import AblePayrollImportWizard from './components/AblePayrollImportWizard';
import ViolationTrendsChart from './components/ViolationTrendsChart';
import CostImpactAnalysisPanel from './components/CostImpactAnalysisPanel';
import ScheduledDeliveryManager from './components/ScheduledDeliveryManager';
import BiometricSecurityDisclaimer from '../../components/navigation/BiometricSecurityDisclaimer';

const ComplianceLibrary = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('documents');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportPeriodFilter, setReportPeriodFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState('');
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [selectedReportConfig, setSelectedReportConfig] = useState(null);
  const [aiPredictions, setAiPredictions] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [claudeBenchmarkAnalysis, setClaudeBenchmarkAnalysis] = useState(null);
  const [loadingClaudeBenchmark, setLoadingClaudeBenchmark] = useState(false);

  useEffect(() => {
    if (activeTab === 'reports') {
      loadReports();
    }
    loadAIPredictions();
  }, [activeTab, reportPeriodFilter]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError('');

      let reportsData;
      if (reportPeriodFilter === 'all') {
        reportsData = await complianceReportService?.getAllReports();
      } else {
        reportsData = await complianceReportService?.getReportsByPeriodType(reportPeriodFilter);
      }

      setReports(reportsData);
    } catch (err) {
      setError(err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadAIPredictions = async () => {
    try {
      setLoadingPredictions(true);
      const predictions = await aiComplianceService?.predictComplianceRisks();
      setAiPredictions(predictions);
    } catch (err) {
      console.error('Failed to load AI predictions:', err);
    } finally {
      setLoadingPredictions(false);
    }
  };

  const handleViewReportDetails = (reportId) => {
    navigate(`/compliance-reports/${reportId}`);
  };

  const handleExportReport = (reportId) => {
    navigate(`/compliance-reports/${reportId}`);
  };

  const handleRunAiAnalysis = async () => {
    try {
      setAiAnalysisLoading(true);
      setAiAnalysisError('');

      const analysis = await aiComplianceService?.runCompleteAnalysis(null);
      setAiAnalysis(analysis);
    } catch (err) {
      setAiAnalysisError(err?.message || 'Failed to run AI analysis');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleClaudeBenchmarkAnalysis = async () => {
    try {
      setLoadingClaudeBenchmark(true);
      
      const complianceData = {
        totalViolations: reports?.length,
        recentReports: reports?.slice(0, 10),
        complianceScore: 85
      };
      
      const result = await claudeComplianceService?.compareAgainstBenchmarks(complianceData, 'general');
      setClaudeBenchmarkAnalysis(result);
    } catch (err) {
      console.error('Failed to run Claude benchmark analysis:', err);
    } finally {
      setLoadingClaudeBenchmark(false);
    }
  };

  const handleReportGenerated = (config) => {
    setSelectedReportConfig(config);
    setActiveTab('reports');
  };

  const handleImportComplete = (result) => {
    setShowImportWizard(false);
    setActiveTab('documents');
  };

  const periodFilterOptions = [
    { value: 'all', label: 'All Reports' },
    { value: 'monthly', label: 'Monthly Reports' },
    { value: 'quarterly', label: 'Quarterly Reports' },
    { value: 'annual', label: 'Annual Reports' }
  ];

  const tabs = [
    { id: 'documents', label: 'Compliance Documents', icon: 'FileText' },
    { id: 'reports', label: 'Board Reports', icon: 'BarChart3' },
    { id: 'ai-analysis', label: 'AI Analysis', icon: 'Sparkles' },
    { id: 'remediation', label: 'Remediation Status', icon: 'Shield' },
    { id: 'lodgement', label: 'Document Lodgement', icon: 'FileCheck' },
    { id: 'contacts', label: 'Regulatory Contacts', icon: 'Users' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <BiometricSecurityDisclaimer />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            Compliance Library
          </h1>
          <p className="caption text-sm md:text-base text-muted-foreground">
            Access PNG employment regulations, board-ready reports, and regulatory contacts
          </p>
        </div>

        {/* New Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            iconName="Upload"
            onClick={() => setShowImportWizard(true)}
          >
            Import from ABLE Payroll
          </Button>
          <Button
            variant="outline"
            iconName="FileText"
            onClick={() => setActiveTab('irc-forms')}
          >
            Generate IRC SWT Form
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex items-center gap-1">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} className="inline mr-2" />
                {tab?.label}
              </button>
            ))}
            <button
              onClick={() => setActiveTab('customization')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'customization' ? 'bg-card text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="Settings" size={16} className="inline mr-2" />
              Customization
            </button>
            <button
              onClick={() => setActiveTab('scheduled')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'scheduled' ? 'bg-card text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="Clock" size={16} className="inline mr-2" />
              Scheduled Delivery
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'customization' && (
          <div className="space-y-6">
            <ReportCustomizationPanel onReportGenerated={handleReportGenerated} />
            
            {selectedReportConfig && (
              <>
                {selectedReportConfig?.includeViolationTrends && (
                  <ViolationTrendsChart
                    periodStart={selectedReportConfig?.periodStart}
                    periodEnd={selectedReportConfig?.periodEnd}
                  />
                )}
                
                {selectedReportConfig?.includeCostAnalysis && (
                  <CostImpactAnalysisPanel reportId={selectedReportConfig?.id} />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'scheduled' && (
          <ScheduledDeliveryManager />
        )}

        {activeTab === 'documents' && (
          <div className="bg-card border border-border rounded-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="FileText" size={24} color="var(--color-primary)" />
              <h2 className="font-heading font-semibold text-xl text-foreground">
                PNG Employment Act 1978 & Regulations
              </h2>
            </div>
            <p className="caption text-sm text-muted-foreground mb-6">
              Access official compliance documents, regulations, and policy guidelines for PNG employment law.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  Employment Act 1978
                </h3>
                <p className="caption text-xs text-muted-foreground mb-3">
                  Core legislation governing employment relationships in Papua New Guinea
                </p>
                <a 
                  href="https://www.labour.gov.pg/download/employment-act-1978/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    View Document
                  </Button>
                </a>
              </div>
              <div className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  Minimum Wage Determination
                </h3>
                <p className="caption text-xs text-muted-foreground mb-3">
                  Current minimum wage rates and determination references
                </p>
                <a 
                  href="https://www.ilo.org/resource/news/ilo-welcomes-new-national-minimum-wage-papua-new-guinea" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    View Document
                  </Button>
                </a>
              </div>
              <div className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  NASFUND Portal
                </h3>
                <p className="caption text-xs text-muted-foreground mb-3">
                  National Superannuation Fund online portal for contributions and compliance
                </p>
                <a 
                  href="https://www.nasfund.com.pg" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    Visit Portal
                  </Button>
                </a>
              </div>
              <div className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  IRC Tax Portal
                </h3>
                <p className="caption text-xs text-muted-foreground mb-3">
                  Internal Revenue Commission portal for tax compliance and SWT forms
                </p>
                <a 
                  href="https://www.irc.gov.pg" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    Visit Portal
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Icon name="BarChart3" size={24} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-xl text-foreground">
                  Board-Ready Compliance Reports
                </h2>
              </div>
              <Select
                options={periodFilterOptions}
                value={reportPeriodFilter}
                onChange={setReportPeriodFilter}
                className="w-48"
              />
            </div>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
              </div>
            )}

            {error && (
              <div className="bg-error/10 border border-error rounded-lg p-6 text-center">
                <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-3" />
                <p className="text-error font-medium">{error}</p>
              </div>
            )}

            {!loading && !error && (
              <div className="grid grid-cols-1 gap-6">
                {reports?.map(report => (
                  <ComplianceReportCard
                    key={report?.id}
                    report={report}
                    onViewDetails={handleViewReportDetails}
                    onExport={handleExportReport}
                  />
                ))}

                {reports?.length === 0 && (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
                    <p className="caption text-sm text-muted-foreground">No compliance reports available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'ai-analysis' && (
          <div>
            {aiAnalysisError && (
              <div className="bg-error/10 border border-error rounded-lg p-6 mb-6">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                  <p className="text-error font-medium">{aiAnalysisError}</p>
                </div>
              </div>
            )}

            <AiComplianceAnalysisCard
              analysis={aiAnalysis}
              onRunAnalysis={handleRunAiAnalysis}
              loading={aiAnalysisLoading}
            />
          </div>
        )}

        {activeTab === 'remediation' && (
          <RemediationStatusPanel />
        )}

        {activeTab === 'lodgement' && (
          <DocumentLodgementPanel />
        )}

        {activeTab === 'contacts' && (
          <div className="bg-card border border-border rounded-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Users" size={24} color="var(--color-primary)" />
              <h2 className="font-heading font-semibold text-xl text-foreground">
                Regulatory Contacts
              </h2>
            </div>
            <p className="caption text-sm text-muted-foreground mb-6">
              Contact information for PNG Department of Labour and Industrial Relations (DLIR) and other regulatory bodies.
            </p>
            <div className="space-y-4">
              <div className="border border-border rounded-lg p-4">
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                  Department of Labour & Industrial Relations (DLIR)
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon name="Phone" size={14} color="var(--color-muted-foreground)" />
                    <p className="caption text-sm text-muted-foreground">+675 321 5600</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={14} color="var(--color-muted-foreground)" />
                    <a href="mailto:info@labour.gov.pg" className="caption text-sm text-primary hover:underline">
                      info@labour.gov.pg
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                    <p className="caption text-sm text-muted-foreground">Port Moresby, National Capital District</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Globe" size={14} color="var(--color-muted-foreground)" />
                    <a 
                      href="https://www.dlir.gov.pg" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="caption text-sm text-primary hover:underline"
                    >
                      www.dlir.gov.pg
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ABLE Payroll Import Wizard Modal */}
      {showImportWizard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <AblePayrollImportWizard
            onComplete={handleImportComplete}
            onCancel={() => setShowImportWizard(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ComplianceLibrary;